import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { FeedPost } from '../types/social';

interface FeedStore {
  posts: FeedPost[];
  loading: boolean;
  error: string | null;
  fetchPosts: () => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
}

export const useFeedStore = create<FeedStore>((set) => ({
  posts: [],
  loading: false,
  error: null,

  fetchPosts: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ posts: [] });
        return;
      }

      const { data, error } = await supabase
        .from('feed_posts')
        .select(`
          *,
          user_profile:user_profiles!feed_posts_user_id_fkey (
            id,
            email,
            display_name,
            avatar_url
          ),
          recipe:recipes!feed_posts_recipe_id_fkey (
            title,
            image_url
          ),
          meal:meals!feed_posts_meal_id_fkey (
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      set({
        posts: (data || []).map(post => ({
          ...post,
          user_profile: post.user_profile,
          recipe_title: post.recipe?.title,
          image_url: post.recipe?.image_url || post.image_url,
          meal_name: post.meal?.name
        }))
      });
    } catch (err) {
      console.error('Error in fetchPosts:', err);
      set({ 
        posts: [],
        error: err instanceof Error ? err.message : 'Failed to fetch posts'
      });
    } finally {
      set({ loading: false });
    }
  },

  deletePost: async (postId: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('feed_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
      await set.getState().fetchPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to delete post' });
    } finally {
      set({ loading: false });
    }
  }
}));